import { useState, useEffect } from 'react';
import { FunContentItem } from '../../types/dashboard';

interface FunSectionPanelProps {
  items: FunContentItem[];
  cycleInterval?: number; // ms, default 15000
}

const CATEGORY_LABELS: Record<FunContentItem['category'], string> = {
  'cultural-fact': 'Cultural Fact',
  'reunion-trivia': 'Reunion Trivia',
  'member-spotlight': 'Member Spotlight',
  'motivational-quote': 'Motivational Quote',
};

// Color palette for booklet sections
const SECTION_COLORS: Record<string, string> = {
  'Front Matter': '#e91e63',
  'Feature Stories': '#9c27b0',
  'Heritage & History': '#673ab7',
  'Faith & Culture': '#3f51b5',
  'People & Community': '#2196f3',
  'Culture & Lifestyle': '#009688',
  'KDU Global Impact': '#4caf50',
  'Looking Forward': '#ff9800',
  'Unassigned': '#607d8b',
};

function getSectionColor(section: string): string {
  return SECTION_COLORS[section] || SECTION_COLORS['Unassigned'];
}

function ArticlesDisplay({ articles }: { articles: { title: string; section: string; author: string }[] }) {
  return (
    <div style={{ textAlign: 'left', maxHeight: '55vh', overflowY: 'auto', width: '100%', scrollbarWidth: 'none' }}>
      {articles.map((article, idx) => (
        <div
          key={idx}
          style={{
            marginBottom: '10px',
            padding: '8px 10px',
            borderLeft: `3px solid ${getSectionColor(article.section)}`,
            background: 'rgba(255,255,255,0.04)',
            borderRadius: '0 4px 4px 0',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '3px' }}>
            {article.title}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
            <span
              style={{
                fontSize: '10px',
                padding: '2px 6px',
                borderRadius: '3px',
                backgroundColor: getSectionColor(article.section),
                color: '#fff',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {article.section}
            </span>
            <span style={{ fontSize: '11px', color: '#e0e0e0', fontStyle: 'italic' }}>
              {article.author}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function FunSectionPanel({ items, cycleInterval = 15000 }: FunSectionPanelProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (items.length === 0) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, cycleInterval);
    return () => clearInterval(timer);
  }, [items.length, cycleInterval]);

  // Reset index if items change and current index is out of bounds
  useEffect(() => {
    if (items.length > 0 && currentIndex >= items.length) {
      setCurrentIndex(0);
    }
  }, [items.length, currentIndex]);

  if (items.length === 0) return null;

  const item = items[currentIndex];
  const hasArticles = item.articles && item.articles.length > 0;

  return (
    <aside
      style={{
        width: 320,
        height: '100vh',
        background: '#0d1b12',
        borderLeft: '2px solid #1a6b3c',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '32px 24px',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          maxWidth: '100%',
          width: '100%',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            marginBottom: 16,
            padding: '6px 14px',
            borderRadius: 4,
            background: 'rgba(201, 168, 76, 0.15)',
            color: '#c9a84c',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontFamily: "'Inter', system-ui, sans-serif",
          }}
        >
          {CATEGORY_LABELS[item.category] ?? item.category}
        </span>
        {item.imageUrl && (
          <img
            src={item.imageUrl}
            alt=""
            style={{
              display: 'block',
              width: 120,
              height: 120,
              borderRadius: '50%',
              objectFit: 'cover',
              margin: '0 auto 16px auto',
              border: '2px solid rgba(201, 168, 76, 0.4)',
            }}
          />
        )}
        <p
          style={{
            color: '#ffffff',
            fontSize: hasArticles ? '16px' : (item.text.length > 200 ? '14px' : '20px'),
            lineHeight: 1.5,
            margin: '0 0 12px 0',
            fontFamily: "'Inter', system-ui, sans-serif",
            whiteSpace: 'pre-line',
            textAlign: 'center',
          }}
        >
          {item.text}
        </p>
        {hasArticles && <ArticlesDisplay articles={item.articles!} />}
        {item.attribution && !hasArticles && (
          <p
            style={{
              color: '#e0e0e0',
              fontSize: '14px',
              fontStyle: 'italic',
              margin: 0,
              fontFamily: "'Inter', system-ui, sans-serif",
            }}
          >
            — {item.attribution}
          </p>
        )}
      </div>
    </aside>
  );
}
